import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handleRequest } from '../quipli-service-client';
import { AxiosRequestConfig } from 'axios';
import {
  UpsertRoleRequest,
  Representative,
  RepresentativeRole,
  RepresentativeSearchRequest,
  UpsertRepresentativeRequest,
  SearchRolesRequest,
} from './representative';
import { RepresentativeParsers } from './representative-parsers';
import { Pagination } from '../pagination/pagination';
import { PaginationParsers } from '../pagination/pagination-parsers';

export const useRepresentativeApi = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function searchRoles(req: SearchRolesRequest): Promise<Pagination<RepresentativeRole>> {
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/representatives/roles/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    const { items, pagination } = await handleRequest<
      PaginationParsers.PaginationJson<RepresentativeParsers.RepresentativeRoleJson>
    >(config);
    return {
      items: items.map(RepresentativeParsers.toRepresentativeRole),
      pagination,
    };
  }

  async function getRole(roleId: string): Promise<RepresentativeRole> {
    const config: AxiosRequestConfig = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/representatives/roles/${roleId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return RepresentativeParsers.toRepresentativeRole(
      await handleRequest<RepresentativeParsers.RepresentativeRoleJson>(config),
    );
  }

  async function createRole(req: UpsertRoleRequest): Promise<RepresentativeRole> {
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/representatives/roles`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return RepresentativeParsers.toRepresentativeRole(
      await handleRequest<RepresentativeParsers.RepresentativeRoleJson>(config),
    );
  }

  async function updateRole(roleId: string, req: UpsertRoleRequest): Promise<RepresentativeRole> {
    const config: AxiosRequestConfig = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/representatives/roles/${roleId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return RepresentativeParsers.toRepresentativeRole(
      await handleRequest<RepresentativeParsers.RepresentativeRoleJson>(config),
    );
  }

  async function deleteRepresentative(rep: Representative): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/users/${rep.userId}/representatives/${rep.id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: rep,
    };
    return await handleRequest(config);
  }

  async function getRepresentatives(userId: number): Promise<Representative[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/representatives`,
    };
    return await handleRequest<Representative[]>(config);
  }

  async function searchRepresentatives(
    userId: number,
    request: RepresentativeSearchRequest,
  ): Promise<Pagination<Representative>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/representatives/search`,
      data: request,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<Representative>>(config);
    return {
      pagination,
      items,
    };
  }

  async function upsertRepresentative(req: UpsertRepresentativeRequest): Promise<Representative> {
    const config = {
      method: req.id ? 'put' : 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${req.userId}/representatives/${req.id ?? ''}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    return RepresentativeParsers.toRepresentative(
      await handleRequest<RepresentativeParsers.RepresentativeJson>(config),
    );
  }

  return {
    searchRoles,
    getRole,
    createRole,
    updateRole,
    deleteRepresentative,
    getRepresentatives,
    searchRepresentatives,
    upsertRepresentative,
  };
};
